import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { verifyEmail } from '../../services/loyaltyEmailVerificationApi';
import { FONT_FAMILY } from '../../theme';

export default function EmailVerification() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('');
    const [errorDetails, setErrorDetails] = useState(null);

    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const appDeepLink = token && email ? `shishavibes://loyalty/verify-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}` : null;

    useEffect(() => {
        // If opened on mobile browser, try to launch the installed Shisha Vibes mobile application
        if (appDeepLink && /android|iphone|ipad/i.test(navigator.userAgent)) {
            window.location.href = appDeepLink;
        }
    }, [appDeepLink]);

    useEffect(() => {
        const verify = async () => {
            if (!token || !email) {
                setStatus('error');
                setMessage(t('loyalty.verification.invalidToken') || 'Invalid verification token');
                return;
            }

            try {
                const response = await verifyEmail(email, token);
                setStatus('success');
                setMessage(response.message || t('loyalty.verification.success') || 'Email verified successfully!');
            } catch (error) {
                setStatus('error');
                setMessage(error.message || t('loyalty.verification.failed') || 'Email verification failed');
                setErrorDetails(error);
            }
        };

        verify();
    }, [searchParams, t, token, email]);

    const handleGoToLogin = () => {
        navigate('/loyalty/login', {
            state: { message: t('loyalty.verification.successMessage') || 'Email verified! You can now login.' }
        });
    };

    const handleResend = () => {
        navigate('/loyalty/resend-verification', {
            state: { email: searchParams.get('email') }
        });
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1e2225 0%, #2a2d31 100%)',
                padding: 3,
                fontFamily: FONT_FAMILY
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    backgroundColor: '#2a2d31',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
            >
                <CardContent sx={{ padding: 4, textAlign: 'center' }}>
                    {status === 'verifying' && (
                        <>
                            <CircularProgress
                                size={60}
                                sx={{ color: '#ff6b00', mb: 3 }}
                            />
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    mb: 2,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {t('loyalty.verification.verifying') || 'Verifying your email...'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 3,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {t('loyalty.verification.pleaseWait') || 'Please wait while we verify your email address.'}
                            </Typography>
                            {appDeepLink && (
                                <Button
                                    variant="contained"
                                    href={appDeepLink}
                                    sx={{
                                        backgroundColor: '#C5A059',
                                        color: '#121212',
                                        padding: '10px 24px',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        fontFamily: FONT_FAMILY,
                                        '&:hover': {
                                            backgroundColor: '#DFBA6D',
                                        }
                                    }}
                                >
                                    📱 Отвори в Shisha Vibes App
                                </Button>
                            )}
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircleIcon
                                sx={{
                                    fontSize: 80,
                                    color: '#4caf50',
                                    mb: 3
                                }}
                            />
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    mb: 2,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {t('loyalty.verification.successTitle') || 'Email Verified!'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 4,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {message}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                {appDeepLink && (
                                    <Button
                                        variant="contained"
                                        href={appDeepLink}
                                        sx={{
                                            backgroundColor: '#C5A059',
                                            color: '#121212',
                                            padding: '12px 30px',
                                            fontSize: '1rem',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            width: '100%',
                                            maxWidth: 320,
                                            fontFamily: FONT_FAMILY,
                                            '&:hover': {
                                                backgroundColor: '#DFBA6D',
                                            }
                                        }}
                                    >
                                        📱 Отвори в мобилното приложение
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    onClick={handleGoToLogin}
                                    sx={{
                                        borderColor: '#ff6b00',
                                        color: '#ff6b00',
                                        padding: '12px 30px',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        width: '100%',
                                        maxWidth: 320,
                                        fontFamily: FONT_FAMILY,
                                        '&:hover': {
                                            borderColor: '#ff8533',
                                            backgroundColor: 'rgba(255, 107, 0, 0.1)',
                                        }
                                    }}
                                >
                                    {t('loyalty.verification.goToLogin') || 'Вход през браузъра'}
                                </Button>
                            </Box>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <ErrorIcon
                                sx={{
                                    fontSize: 80,
                                    color: '#f44336',
                                    mb: 3
                                }}
                            />
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    mb: 2,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {t('loyalty.verification.errorTitle') || 'Verification Failed'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 4,
                                    fontFamily: FONT_FAMILY
                                }}
                            >
                                {message}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleResend}
                                    sx={{
                                        backgroundColor: '#ff6b00',
                                        color: '#fff',
                                        padding: '12px 30px',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        fontFamily: FONT_FAMILY,
                                        '&:hover': {
                                            backgroundColor: '#ff8533',
                                        }
                                    }}
                                >
                                    {t('loyalty.verification.resendEmail') || 'Resend Verification Email'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/loyalty/login')}
                                    sx={{
                                        borderColor: '#ff6b00',
                                        color: '#ff6b00',
                                        padding: '12px 30px',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        fontFamily: FONT_FAMILY,
                                        '&:hover': {
                                            borderColor: '#ff8533',
                                            backgroundColor: 'rgba(255, 107, 0, 0.1)',
                                        }
                                    }}
                                >
                                    {t('loyalty.verification.backToLogin') || 'Back to Login'}
                                </Button>
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
